import { TreeNode } from "primeng/api";

// Windows volumes and resources types
export enum DriveType {
  Fixed = "Fixed",
  Removable = "Removable",
}

export interface GetVolumeResult {
  DriveLetter: string;
  FileSystemType: string;
  FriendlyName: string;
  Size: number;
  SizeRemaining: number;
  DriveType: DriveType;
}

export interface GetChildItemResult {
  Name: string;
  FullName: string;
  Length: number;
  IsDirectory: boolean;
}

// UI Volumes and Resources Types
export type VolumeInfo = {
  driveLetter: string;
  driveType: DriveType;
  fileSystemType: string;
  friendlyName: string;
  size: number;
  sizeRemaining: number;
};

export interface FileSystemNodeInfo {
  name: string;
  fullName: string;
  length: number;
  isDirectory: boolean;
}

export enum FileSystemObjectType {
  Volume = "volume",
  Folder = "folder",
  File = "file",
}

export type FileInfo = {
  folderPath: string;
  fileName: string;
}

export type FileStats = FileInfo & {
  exists: boolean;
};

export type ReadFileData = {
  filePath: string;
  fileName: string;
  mimeType: string;
  data: Buffer;
};

export type WriteFileRequest = {
  folderPath: string;
  fileName: string;
  mimeType: string;
  data: Buffer;
};

export enum OperationMode {
  Import = "import",
  Export = "export",
}

export enum SelectionMode {
  SingleFile = "single-file",
  MultipleFiles = "multiple-files",
  SingleFolder = "single-folder",
  MultipleFolders = "multiple-folders",
  SingleFileOrFolder = "single-file-or-folder",
  MultipleFileOrFolders = "multiple-files-or-folders",
}

export type FileExtensions =
| string[]
| Set<string>
| Record<string, string>
| { [extension: string]: string };

// File System Tree Representation
export type FileSystemObject =
| {
  type: FileSystemObjectType.Volume;
  driveType: DriveType;
  totalSpace: number;
  freeSpace: number;
}
| {
  type: FileSystemObjectType.File | FileSystemObjectType.Folder;
  isDirectory: boolean;
  size: number;
};

export type FileSystemTreeNode = TreeNode<FileSystemObject>;

// Utility to require at least one of the given properties of the parameter type
type RequireAtLeastOne<T, Keys extends keyof T = keyof T> =
  Keys extends keyof T
    ? Required<Pick<T, Keys>> & Partial<Omit<T, Keys>>
    : never;

// Access control
export enum AccessGrant {
  Allow = "allow",
  Deny = "deny",
  NotDetermined = "not-determined"
}

export enum AccessRuleType {
  Drive = "drive",
  Folder = "folder",
  File = "file",
}

export enum AccessRuleOperation {
  And = "AND",
  Or = "OR",
  Not = "NOT",
}

export type AccessRule =
  | (
    {
      type: AccessRuleType.Drive;
      defaultContentAccess: Exclude<AccessGrant, AccessGrant.NotDetermined>;
    } & RequireAtLeastOne<{ driveLetter?: string; driveType?: DriveType; }, "driveLetter" | "driveType">
    |
    {
      type: AccessRuleType.Folder;
      folderPath: string;
      includeSubfolders: boolean
    }
    |
    {
      type: AccessRuleType.File;
      filePath: string;
    }
  ) & {
    access: AccessGrant,
  }
| {
    operation: AccessRuleOperation;
    rules: AccessRule[];
  };
